<template>
  <div>
    <h2 class="step-name">Mastitis</h2>

    <div class="step-section">
      <h3>Dry Cow Used</h3>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Herd</th>
            <th>Product</th>
            <th>{{ seasonStartYear - 1 }} / {{ seasonStartYear }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(rv, index) in model.CHARTS.drycow_sales" :key="index">
            <td class="text-left">{{ rv["Herd"] }}</td>
            <td class="text-left">{{ rv["Product"] }}</td>
            <td class="text-left">{{ rv["qty"] }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="step-section">
      <h3>Cows Treated Spring Mastitis</h3>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Season</th>
            <th>Total Cows Wintered</th>
            <th>Cows Treated</th>
            <th>Percent Cows Wintered</th>
            <th>Percent Region Avg</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(rv, index) in model.CHARTS.spring_mastitis" :key="index"
            :class="getRowClass(model.CHARTS.spring_mastitis, 'Season', index)">

            <td class="text-left">{{ rv["Season"] }}</td>
            <td class="text-left">{{ rv["Total Cows Wintered"] }}</td>
            <td class="text-left">{{ rv["Cows Treated"] }}</td>
            <td class="text-left">{{ rv["Percent Total Cows Wintered"] }}</td>
            <td class="text-left">{{ rv["Region Avg. Percent Total Cows Wintered"] }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="step-section">
      <h3>Calving Mastitis</h3>
      <br>

      <div class="row form-group">
        <label for="est_percent_mastitis_treat_is_heifers" class="col-sm-6">
          Of the {{ model.CHARTS.spring_mastitis_breakdown[0] ? model.CHARTS.spring_mastitis_breakdown[0]["cows_treated"] : 0 }} cows treated, what <span class="alert-danger">PERCENTAGE</span> where heifers?
        </label>
        <div class="col-sm-3">
          <input type="number"
                 class="form-control"
                 name="est_percent_mastitis_treat_is_heifers"
                 id="est_percent_mastitis_treat_is_heifers"
                 v-model="model.est_percent_mastitis_treat_is_heifers"
                 :disabled="isReadOnly"
                 @input="updateCalvingMastitisValues('percent')">

            <span v-show="errors.has('est_percent_mastitis_treat_is_heifers')" class="text-danger">
              {{ errors.first('est_percent_mastitis_treat_is_heifers') }}
            </span>
        </div>
      </div>

      <p class="text-danger">
      <strong>OR</strong>
      </p>

      <div class="row form-group">
        <label for="est_number_mastitis_treat_is_heifers" class="col-sm-6">
          Of the {{totalCowsTreated}} cows treated, what <span class="alert-danger">NUMBER</span> where heifers?
        </label>
        <div class="col-sm-3">
          <input type="number"
                 class="form-control"
                 name="est_number_mastitis_treat_is_heifers"
                 id="est_number_mastitis_treat_is_heifers"
                 v-model="model.est_number_mastitis_treat_is_heifers"
                 :disabled="isReadOnly"
                 @input="updateCalvingMastitisValues('number')">

            <span v-show="errors.has('est_number_mastitis_treat_is_heifers')" class="text-danger">
              {{ errors.first('est_number_mastitis_treat_is_heifers') }}
            </span>
        </div>
      </div>
      <div class="row form-group">
        <label for="est_percent_mastitis_treat_mixed" class="col-sm-6">Estimated % of treatments that are mixed age</label>
        <div class="col-sm-3">
          <input type="text" class="form-control" :value="estPercentMastitisTreatMixed" disabled>
        </div>
      </div>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Group</th>
            <th>Animal numbers</th>
            <th>Treated</th>
            <th>Percent of group treated</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-left">Total Cows Wintered</td>
            <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0] ? model.CHARTS.spring_mastitis_breakdown[0]["cows_wintered_incl_r2"] : 0 }}</td>
            <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0] ? model.CHARTS.spring_mastitis_breakdown[0]["cows_treated"] : 0 }}</td>
            <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0] ? model.CHARTS.spring_mastitis_breakdown[0]["percent_treated"] : 0 }}</td>
          </tr>
          <tr>
            <td class="text-left">Mixed Age</td>
            <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0] ? model.CHARTS.spring_mastitis_breakdown[0]["mixed_age"] : 0 }}</td>
            <td class="text-left">{{ mixedAgeTreated }}</td>
            <td class="text-left">{{ mixedAgePercentTreated }}</td>
          </tr>
          <tr>
            <td class="text-left">Heifers</td>
            <td class="text-left">{{ model.CHARTS.spring_mastitis_breakdown[0] ? model.CHARTS.spring_mastitis_breakdown[0]["heifers"] : 0}}</td>
            <td class="text-left"> {{ heifersTreated }} </td>
            <td class="text-left"> {{ heifersPercentTreated }} </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="step-section">
      <h3>Mastitis Treatments</h3>
      <bar :chart-data="treatmentsChartData" :options="chartOptions" ></bar>

      <table class="table table&#45;bordered">
        <thead>
          <tr>
            <th></th>
            <th v-for="(season, index) in chartMastitisTreatmentsDates" :key="index"> {{ season }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(product, productIndex) in chartMastitisTreatmentsProducts" :key="productIndex">
            <td>{{ product }}</td>
            <td v-for="(date, dateIndex) in chartMastitisTreatmentsDates" :key="dateIndex">
              {{ getProductValueForDate(product, date) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="step-section">
      <div class="form-group">
        <label for="dcat_keypoints">Key Points</label>
        <textarea rows="5" cols="50" v-model="model.dcat_keypoints" class="form-control" :disabled="isReadOnly">
        </textarea>
      </div>

      <div class="form-group">
        <label for="dcat_actions">Actions</label>
        <textarea rows="5" cols="50" v-model="model.dcat_actions" class="form-control" :disabled="isReadOnly">
        </textarea>
      </div>
    </div>

  </div>
</template>

<script>
import baseComponent from '@/mixins/base_consult_component'
import Bar from '../../charts/Bar'

export default {
  mixins: [ baseComponent ],
  components: {
    Bar,
  },
  data() {
    return {
      model: {
        dcat_keypoints: "",
        dcat_actions: "",
        est_percent_mastitis_treat_is_heifers: "",
        FARMS: [],
        CHARTS: {
          drycow_sales: [],
          spring_mastitis: [],
          spring_mastitis_treatments: [],
          spring_mastitis_breakdown: [],
        },
      },
    }
  },
  methods: {
    updateCalvingMastitisValues: function (inputType) {

          let cowsTreated = this.totalCowsTreated

          if (inputType == 'percent') {
            let fixed = (this.model['est_percent_mastitis_treat_is_heifers'] * cowsTreated) / 100
            this.model['est_number_mastitis_treat_is_heifers'] = Math.round(fixed, 1)
          } else if (inputType == 'number') {
            let percent = (this.model['est_number_mastitis_treat_is_heifers'] * 100) / cowsTreated
            this.model['est_percent_mastitis_treat_is_heifers'] = Math.round(percent, 1)
          }
        },
    getProductValueForDate: function(product, season) {
      var value = this.model.CHARTS.spring_mastitis_treatments.filter(x => x['Season'] == season && x['Product'] == product)
      if (value.length > 0) {
        return value[0]['Cows Treated']
      }
      return 0
    },
    widthForRow(farms) {
      return farms.length * 500;
    },
    validateIntended: function(farmId){
      var total = this.model[`farm_${farmId}-in_la_dcat`] +
                  this.model[`farm_${farmId}-in_sa_dcat`] +
                  this.model[`farm_${farmId}-in_nothing`] +
                  this.model[`farm_${farmId}-in_herd_its_only`] +
                  this.model[`farm_${farmId}-in_la_its`] +
                  this.model[`farm_${farmId}-in_sa_its`];

      if (total != 100) {
        this.errors.add({
          field: `farm_${farmId}-intended_mmq_error`,
          msg: 'Sum of intended percentages need to add to 100.'
        })
      } else {
        this.errors.remove(`farm_${farmId}-intended_mmq_error`)
      }
    },
  },
  computed: {
    totalCowsTreated: function ()
    {
      return (this.model.CHARTS.spring_mastitis_breakdown[0])
        ? this.model.CHARTS.spring_mastitis_breakdown[0]["cows_treated"]
        : 0
    },
    canViewNextSection: function ()
    {
        return (this.isReadOnly)
            ? true
            : !this.errors.has('est_percent_mastitis_treat_is_heifers')
    },
    estPercentMastitisTreatMixed: function() {
      if (this.model.est_percent_mastitis_treat_is_heifers) {
        return 100 - this.model.est_percent_mastitis_treat_is_heifers
      }
      return 0
    },
    mixedAgeTreated: function() {
      return Math.round(this.estPercentMastitisTreatMixed / 100 * this.totalCowsTreated, 1)
    },
    mixedAgePercentTreated: function() {
      let mixedAge

      if (this.model.CHARTS.spring_mastitis_breakdown[0]){
        mixedAge = this.model.CHARTS.spring_mastitis_breakdown[0]['mixed_age']
      } else {
        mixedAge = 0
      }

      return Math.round(this.mixedAgeTreated / mixedAge * 100, 1)
    },
    heifersTreated: function() {
      return Math.round(this.model.est_percent_mastitis_treat_is_heifers / 100 * this.totalCowsTreated, 1)
    },
    heifersPercentTreated: function() {
      let heifers

      if (this.model.CHARTS.spring_mastitis_breakdown[0]) {
        heifers = this.model.CHARTS.spring_mastitis_breakdown[0]["heifers"]
      } else {
        heifers = 0
      }

      return Math.round(this.heifersTreated / heifers * 100, 1)
    },
    chartOptions: function () {
      return {
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true,
            categoryPercentage: 0.5,
            barPercentage: 1
          }],
          yAxes: [{
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: 'Cows Treated'
            }
          }]
        }
      }
    },

    chartMastitisTreatmentsDates: function() {
      return Array.from(new Set(this.model.CHARTS.spring_mastitis_treatments.map(x => x['Season']))).reverse()
    },

    chartMastitisTreatmentsProducts: function() {
      return Array.from(new Set(this.model.CHARTS.spring_mastitis_treatments.map(x => x['Product'])))
    },

    treatmentsChartData: function() {
      var self = this;

      var seasons  = this.chartMastitisTreatmentsDates
      var products = this.chartMastitisTreatmentsProducts

      var datasets = []

      products.forEach(function(productName) {
        var product = {
          label: productName,
          backgroundColor: '#'+(Math.random()*0xFFFFFF<<0).toString(16),
          data: []
        }
        seasons.forEach(function(seasonName){
          product['data'].push(self.getProductValueForDate(productName, seasonName))
        })

        datasets.push(product)
      })

      return {
        labels: seasons,
        datasets: datasets
      }
    },
  },
  watch:
  {
    canViewNextSection: function ()
    {
      this.$emit('block-next-section', !this.canViewNextSection)
    }
  }
};
</script>

<style scoped>
.farm-fields {
  width: 450px;
  float: left;
  margin-right: 3em;
}
</style>
